import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@diwata/ironvale'
import { DiwaAPI } from '@/api/client'
import { MissionsAPI, type Mission, type MissionProposal } from '@/api/missions'
import { ProposalCard } from '@/components/ProposalCard'
import { useMissions } from '@/store/missions'
import { useMissionSelection } from '@/store/missionSelection'
import { usePrefill } from '@/store/prefill'
import { useUI } from '@/store/ui'
import { HelpHint } from '@/components/HelpHint'

// The Control Plane's mission queue: every mission as a compact row, with a ◈ badge when a
// run is halted at a gate — this rail is the operator's decision inbox. Creation is
// goal-first: the machine drafts the mission, the operator judges the draft.

const OUTCOME_GLYPH: Record<string, string> = { won: '◆', lost: '✕', no_bid: '—', done: '●' }

interface OutcomeStats {
  domains: { domain: string; outcomes: Record<string, number>; win_rate: number | null }[]
}

/** Closed missions are history, not clutter — and history gets its stats line. */
function MissionHistory({
  missions,
  onSelect,
  selectedId,
}: {
  missions: Mission[]
  onSelect: (id: string) => void
  selectedId: string | null
}) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const { data: stats } = useQuery({
    enabled: missions.length > 0,
    queryKey: ['outcome-stats'],
    queryFn: () => api.get<OutcomeStats>('/missions/outcomes/stats'),
  })
  if (missions.length === 0) return null
  return (
    <div style={{ marginTop: 14 }}>
      <div className="dw-rail-section-title">History</div>
      {stats && stats.domains.some((d) => d.win_rate != null) && (
        <div className="dw-inbox-detail" style={{ marginBottom: 6 }}>
          {stats.domains
            .filter((d) => d.win_rate != null)
            .map((d) => `${d.domain}: ${Math.round((d.win_rate as number) * 100)}% won of decided`)
            .join(' · ')}
        </div>
      )}
      <div className="dw-mission-queue">
        {missions.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`dw-queue-row${selectedId === m.id ? ' dw-queue-row--active' : ''}`}
            onClick={() => onSelect(m.id)}
          >
            <span className="dw-queue-glyph">{OUTCOME_GLYPH[m.outcome ?? ''] ?? '●'}</span>
            <span className="dw-queue-title">
              {m.title}
              <span className="dw-inbox-detail">{(m.outcome ?? '').replace('_', '-')}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function glyphFor(m: Mission): { glyph: string; cls: string } {
  if (m.steps.some((s) => s.status === 'awaiting_gate')) return { glyph: '◈', cls: 'gate' }
  if (m.steps.some((s) => s.status === 'failed')) return { glyph: '✕', cls: 'failed' }
  if (m.steps.every((s) => s.status === 'done') && m.steps.length > 0)
    return { glyph: '●', cls: 'done' }
  if (m.steps.some((s) => s.status === 'running' || s.status === 'done'))
    return { glyph: '◉', cls: 'running' }
  return { glyph: '○', cls: 'pending' }
}

// The queue's own legend, in plain words — one row per glyph the queue actually shows, keyed to
// the same `cls` glyphFor emits (so the swatch colour matches the row). Deliberately shorter and
// plainer than the shared GLYPHS legend: the founder reads the rail at a glance, not a glossary.
const QUEUE_LEGEND: { glyph: string; cls: string; label: string }[] = [
  { glyph: '◈', cls: 'gate', label: 'waiting for your call' },
  { glyph: '◉', cls: 'running', label: 'Tala is working' },
  { glyph: '●', cls: 'done', label: 'done' },
  { glyph: '○', cls: 'pending', label: 'not started' },
]

function QueueLegend() {
  return (
    <div role="list" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', margin: '2px 0 10px' }}>
      {QUEUE_LEGEND.map((d) => (
        <span role="listitem" key={d.cls} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span className={`dw-queue-glyph dw-queue-glyph--${d.cls}`} aria-hidden="true" style={{ fontSize: '10px' }}>{d.glyph}</span>
          <span className="dw-inbox-detail">{d.label}</span>
        </span>
      ))}
    </div>
  )
}

export function MissionQueue() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new MissionsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { missions } = useMissions()
  const { selectedId, select } = useMissionSelection()

  // Tala's live suggestions — surfaced as a count that routes to the full Missions surface where
  // they can be promoted. Same ['mission-proposals'] cache the ◈ Needs-you tab and atlas share.
  const { data: proposals } = useQuery({
    queryKey: ['mission-proposals'],
    queryFn: () => api.listProposals(),
  })
  const proposedCount = proposals?.length ?? 0

  // The rail's one quick lens: everything, or just the missions that are stopped at a gate.
  const [filter, setFilter] = useState<'all' | 'needs-you'>('all')
  const active = missions.filter((m) => !m.outcome)
  const needsYouCount = active.filter((m) => glyphFor(m).cls === 'gate').length
  const shown = filter === 'needs-you' ? active.filter((m) => glyphFor(m).cls === 'gate') : active

  // Selecting a mission from the rail opens its own page (/missions/:id) — the stage is a
  // route now, not an inline panel. Selection state is kept for the row's active highlight.
  function open(id: string) {
    select(id)
    navigate(`/missions/${id}`)
  }

  const [goal, setGoal] = useState('')
  const consumeMissionGoal = usePrefill((st) => st.consumeMissionGoal)
  useEffect(() => {
    const seed = consumeMissionGoal()
    if (seed) setGoal(seed)
  }, [consumeMissionGoal])
  const [proposal, setProposal] = useState<MissionProposal | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function propose() {
    if (!goal.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.propose(goal)
      setProposal(r.proposal)
    } catch {
      setError('The familiar could not draft a mission from that. Try rephrasing the goal.')
    } finally {
      setBusy(false)
    }
  }

  async function create(p: MissionProposal) {
    setBusy(true)
    setError(null)
    try {
      const created = await api.create({
        title: p.title.trim() || p.goal,
        goal: p.goal,
        status: 'Active',
        priority: 'High',
        domain: p.domain,
        target: p.target,
        protocol: p.protocol,
        steps: p.steps,
      })
      setProposal(null)
      setGoal('')
      await qc.invalidateQueries({ queryKey: ['missions'] })
      open(created.id)
    } catch {
      setError('Creating the mission failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <div className="dw-rail-section-title" style={{ margin: 0 }}>
          <HelpHint term="mission">Missions</HelpHint>
        </div>
        {proposedCount > 0 && (
          <Link
            className="dw-link"
            to="/missions"
            title="Tala's live suggestions — promote them on the Missions page"
            style={{ fontSize: 'var(--ae-font-size-xs)', whiteSpace: 'nowrap' }}
          >
            ✦ {proposedCount} suggested →
          </Link>
        )}
      </div>
      {missions.length === 0 && (
        <div className="dw-rail-empty">No missions yet — state a goal below.</div>
      )}
      {active.length > 0 && (
        <>
          <div role="group" aria-label="Filter missions" style={{ display: 'flex', gap: 6, margin: '8px 0' }}>
            {/* A single-choice segmented control — NOT dw-chip--toggle, whose off-state is a
                strikethrough (that means "excluded facet", the wrong semantics for a lens). */}
            <button
              type="button"
              className={`dw-chip${filter === 'all' ? ' dw-chip--on' : ''}`}
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              all · {active.length}
            </button>
            <button
              type="button"
              className={`dw-chip${filter === 'needs-you' ? ' dw-chip--on' : ''}`}
              aria-pressed={filter === 'needs-you'}
              onClick={() => setFilter('needs-you')}
            >
              needs you{needsYouCount > 0 ? ` · ${needsYouCount}` : ''}
            </button>
          </div>
          <QueueLegend />
        </>
      )}
      <div className="dw-mission-queue">
        {shown.map((m) => {
          const { glyph, cls } = glyphFor(m)
          const needsYou = cls === 'gate'
          return (
            <button
              key={m.id}
              type="button"
              className={`dw-queue-row${selectedId === m.id ? ' dw-queue-row--active' : ''}`}
              onClick={() => open(m.id)}
            >
              <span className={`dw-queue-glyph dw-queue-glyph--${cls}`}>{glyph}</span>
              <span className="dw-queue-title">{m.title}</span>
              {needsYou && <span className="dw-queue-badge">needs you</span>}
            </button>
          )
        })}
        {active.length > 0 && shown.length === 0 && filter === 'needs-you' && (
          <div className="dw-rail-empty">Nothing needs your call right now.</div>
        )}
      </div>

      <MissionHistory missions={missions.filter((m) => Boolean(m.outcome))} onSelect={open} selectedId={selectedId} />

      <div className="dw-rail-section-title" style={{ marginTop: 14 }}>
        New mission
      </div>
      {proposal ? (
        <ProposalCard
          proposal={proposal}
          busy={busy}
          onCreate={create}
          onDiscard={() => setProposal(null)}
        />
      ) : (
        <div className="dw-goal-form">
          <textarea
            className="dw-input dw-textarea"
            rows={3}
            placeholder="State the goal — Tala drafts the mission, you judge it."
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
          />
          <Button size="sm" variant="primary" disabled={busy || !goal.trim()} onClick={propose}>
            {busy ? '✦ Drafting…' : '✦ Draft mission'}
          </Button>
        </div>
      )}
      {error && <div className="dw-notice dw-run-error">{error}</div>}
    </div>
  )
}
